import { Button } from '@crehana/compass.ui/v3/button';
import { Textarea } from '@crehana/compass.ui/v3/textarea';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@crehana/forms/components';
import { useForm, zodResolver } from '@crehana/forms/rhf';
import { useRef } from 'react';
import { z } from 'zod';

import type { TAddCommentResult } from '../../../hooks/use-add-request-comment';
import { useTranslation } from '../../../i18n';
import type { TKeys } from '../../../i18n/types';

const COMMENT_MAX_LENGTH = 150;

type TCommentFormValues = {
  comment: string;
};

type TCommentFormProps = {
  onSubmit: (body: string) => Promise<TAddCommentResult>;
  submitting: boolean;
};

const ERROR_KEY_BY_RESULT: Record<
  Exclude<TAddCommentResult, 'success'>,
  TKeys
> = {
  required: 'VALIDATION_REQUIRED',
  tooLong: 'VALIDATION_COMMENT_MAX_LENGTH',
  forbidden: 'REQUEST_DETAIL_COMMENT_ERROR_FORBIDDEN',
  error: 'TOAST_ERROR_GENERIC',
};

// AC-4: the field resets only after the server confirms the comment. Any other
// result keeps the text and shows the reason.
export const CommentForm = ({ onSubmit, submitting }: TCommentFormProps) => {
  const { t } = useTranslation();

  // `t` is not stable across renders: `useTranslation` builds it as a new
  // arrow on every call. Rebuild the schema every render instead of
  // memoizing it, so a live locale change reaches the validation messages.
  // Same reason as `respond-request-dialog.tsx` in TIM-562.
  const schema = z.object({
    comment: z
      .string()
      .trim()
      .min(1, t('VALIDATION_REQUIRED'))
      .max(COMMENT_MAX_LENGTH, t('VALIDATION_COMMENT_MAX_LENGTH')),
  });

  const form = useForm<TCommentFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { comment: '' },
  });

  // The `submitting` prop lags one render behind a fast second submit, and the
  // backend has no idempotency key. The ref closes that gap.
  const sendingRef = useRef(false);

  const handleSubmit = async ({ comment }: TCommentFormValues) => {
    if (submitting || sendingRef.current) {
      return;
    }
    sendingRef.current = true;
    let result: TAddCommentResult;
    try {
      result = await onSubmit(comment);
    } finally {
      sendingRef.current = false;
    }
    if (result === 'success') {
      // Text typed while the request ran must survive the reset.
      if (form.getValues('comment').trim() === comment) {
        form.reset();
      }
      return;
    }
    form.setError('comment', {
      type: 'server',
      message: t(ERROR_KEY_BY_RESULT[result]),
    });
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="tw-flex tw-flex-col tw-cds-gap-2"
      >
        <FormField
          control={form.control}
          name="comment"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('REQUEST_DETAIL_COMMENT_LABEL')}</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  rows={3}
                  maxLength={COMMENT_MAX_LENGTH}
                  error={Boolean(fieldState.error)}
                  placeholder={t('REQUEST_DETAIL_COMMENT_PLACEHOLDER')}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="tw-flex tw-justify-start">
          <Button
            type="submit"
            variant="tertiary"
            size="sm"
            loading={submitting || form.formState.isSubmitting}
          >
            {t('REQUEST_DETAIL_COMMENT_SUBMIT')}
          </Button>
        </div>
      </form>
    </Form>
  );
};
